import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { validateRouteRole } from '@/lib/routing';
import { instantFromDate, instantToDate, isAirAdmin } from '@airreading/common';

import { InfoPage } from '@/components/info-page';
import { SessionDateTimeDivider } from '@/components/session/session-date-time-divider';
import { EntityTableGroupBy } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useFeatureFlag } from '@/hooks/use-feature-flags';
import { lessonTypeaheadSearch, useOrgOptions, useSchoolOptions } from '@/lib/api-call-hooks';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { classTimeTypesFilterOption } from '@/lib/form-utils';
import { paginationRequestSchema } from '@/lib/pagination';
import { pluralize } from '@/lib/string-utils';
import { getStudentYearOptions } from '@/lib/student-utils';
import { groupByStartDateAndTimeRange, StartDateTimeRangeGroupKey } from '@/lib/table-grouping-utils';
import { BulkRefreshZoomLinks } from '@/routes/_layout/sessions/-components/bulk-refresh-zoom-links';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions';
import { CreateSessionModal } from '@/routes/_layout/sessions/-components/create-session-modal';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table';
import { sessionsTabs } from '@/routes/_layout/sessions/-components/tabs';
import { FrequentTimeZones } from '@airreading/common';
import {
  AdminSession,
  classTimeTypeSchema,
  CoachSessionRemarkV2LabelMap,
  coachSessionRemarkV2Schema,
  lessonKindSchema,
  LessonKindToLabel,
  studentYearSchema,
} from '@airreading/common/data';

export const DEFAULT_LIVE_SESSION_PAGE_SIZE = 100;

const liveSessionPaginationRequestSchema = paginationRequestSchema.merge(
  z.object({
    pageSize: z.number().int().default(DEFAULT_LIVE_SESSION_PAGE_SIZE),
  }),
);

export const Route = createFileRoute('/_layout/sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SessionsIndex,
  validateSearch: liveSessionPaginationRequestSchema.extend({
    query: z.string().optional(),
    orgs: z.string().array().optional(),
    schools: z.string().array().optional(),
    grades: z.array(studentYearSchema).optional(),
    lessons: z.string().array().optional(),
    attentionSessions: z.boolean().optional(),
    nextHour: z.boolean().optional(),
    lessonKinds: z.array(lessonKindSchema).optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
    sessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
  }),
});

const TodaySessionsViewMode = {
  All: 'all',
  Attention: 'attention',
  Current: 'current',
  SupportAdmin: 'supportAdmin',
  GoogleMeet: 'googleMeet',
} as const;

type TodaySessionsViewMode = (typeof TodaySessionsViewMode)[keyof typeof TodaySessionsViewMode];

const TODAY_SESSIONS_FILTER_OPTIONS: {
  value: Exclude<TodaySessionsViewMode, typeof TodaySessionsViewMode.All>;
  id: string;
  label: string;
}[] = [
  { value: TodaySessionsViewMode.Attention, id: 'attention-sessions', label: 'Attention sessions' },
  { value: TodaySessionsViewMode.Current, id: 'current-sessions', label: 'Current sessions' },
  { value: TodaySessionsViewMode.SupportAdmin, id: 'support-admin-sessions', label: 'Support admin' },
  { value: TodaySessionsViewMode.GoogleMeet, id: 'google-meet-sessions', label: 'Google Meet' },
];

function isTodaySessionsFilterValue(
  value: string,
): value is Exclude<TodaySessionsViewMode, typeof TodaySessionsViewMode.All> {
  return TODAY_SESSIONS_FILTER_OPTIONS.some((o) => o.value === value);
}

function SessionsIndex() {
  const queryClient = useQueryClient();
  // at least 5000ms, otherwise it's too aggressive
  const refreshIntervalMs = Math.max(useFeatureFlag('refresh-sessions-interval-ms', 5000), 5000);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      lessonKinds: [[selectedLessonKinds, activeSelectedLessonKinds], setSelectedLessonKinds],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      attentionSessions: [[isAttentionSessionsSelected], setIsAttentionSessionsSelected],
      nextHour: [[isNextHourSelected], setIsNextHourSelected],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
      sessionRemarksV2: [[selectedSessionRemarksV2, activeSelectedSessionRemarksV2], setSelectedSessionRemarksV2],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(
    Route.id,
    [
      'query',
      'orgs',
      'schools',
      'grades',
      'lessons',
      'attentionSessions',
      'nextHour',
      'lessonKinds',
      'classTypes',
      'sessionRemarksV2',
    ],
    ['orgs', 'schools', 'classTypes', 'grades', 'lessons', 'lessonKinds', 'sessionRemarksV2'],
  );

  const initialViewMode: TodaySessionsViewMode = isAttentionSessionsSelected
    ? TodaySessionsViewMode.Attention
    : isNextHourSelected
      ? TodaySessionsViewMode.Current
      : TodaySessionsViewMode.All;
  const [viewMode, setViewMode] = useState<TodaySessionsViewMode>(initialViewMode);

  const applyViewMode = useCallback(
    (next: TodaySessionsViewMode) => {
      setViewMode(next);
      setIsAttentionSessionsSelected(next === TodaySessionsViewMode.Attention);
      setIsNextHourSelected(next === TodaySessionsViewMode.Current);
    },
    [setIsAttentionSessionsSelected, setIsNextHourSelected],
  );

  const dateRange = useMemo(() => {
    const today = Temporal.Now.plainDateISO(FrequentTimeZones.UsCentral);
    const startOfToday = today.toZonedDateTime(FrequentTimeZones.UsCentral).startOfDay().toInstant();
    const endOfToday = today.toZonedDateTime(FrequentTimeZones.UsCentral).add({ days: 1 }).startOfDay().toInstant();

    if (isNextHourSelected) {
      const now = Temporal.Now.instant();
      const next30Minutes = instantToDate(now.add({ minutes: 30 }));
      return {
        startOnOrAfter: now.toString(),
        startOnOrBefore: instantFromDate(next30Minutes).toString(),
        endOnOrAfter: now.toString(),
      };
    }

    return {
      startOnOrAfter: startOfToday.toString(),
      startOnOrBefore: endOfToday.toString(),
      endOnOrAfter: undefined,
    };
  }, [isNextHourSelected]);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
    isFetching,
  } = useQuery({
    ...getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: dateRange.startOnOrAfter,
        startOnOrBefore: dateRange.startOnOrBefore,
        endOnOrAfter: dateRange.endOnOrAfter,
        query: activeQuery,
        orgs: activeSelectedOrgs,
        schools: activeSelectedSchools,
        grades: activeSelectedGrades,
        lessons: activeSelectedLessons,
        attentionSessions: isAttentionSessionsSelected,
        nextHour: isNextHourSelected,
        supportAdminSessions: viewMode === TodaySessionsViewMode.SupportAdmin,
        googleMeetSessions: viewMode === TodaySessionsViewMode.GoogleMeet,
        lessonKinds: activeSelectedLessonKinds,
        classTypes: activeSelectedClassTypes,
        sessionRemarksV2: activeSelectedSessionRemarksV2,
      },
      // When filtering by Google Meet sessions only: unchecked first within same class time (for ops live monitoring).
      sortBy: viewMode === TodaySessionsViewMode.GoogleMeet ? ['start-asc', 'googleMeetCheckedAt-asc'] : ['start-asc'],
    }),
    refetchInterval: refreshIntervalMs,
    refetchIntervalInBackground: false,
  });

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = sessionsResp?.adminSessions ?? EmptyArray;
  const rowCount = sessionsResp?.total || 0;
  const allSchoolOptions = useSchoolOptions();
  const allOrgOptions = useOrgOptions();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <BulkRefreshZoomLinks
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Refresh zoom host link', 'Refresh zoom host links')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[200px] lg:w-[300px] xl:w-[350px] 2xl:w-[400px]"
          placeholder="Search sessions by student, teacher, email or lesson name"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />

        <RadioGroup
          className="flex items-center gap-4 w-min"
          value={viewMode === TodaySessionsViewMode.All ? '' : viewMode}
          onValueChange={(value) => {
            if (isTodaySessionsFilterValue(value)) applyViewMode(value);
          }}
        >
          {TODAY_SESSIONS_FILTER_OPTIONS.map(({ value, id, label }) => (
            <div
              key={value}
              className="flex items-center gap-2 cursor-pointer rounded-[6px] bg-white p-3"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                applyViewMode(viewMode === value ? TodaySessionsViewMode.All : value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  applyViewMode(viewMode === value ? TodaySessionsViewMode.All : value);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={viewMode === value}
              aria-label={label}
            >
              <RadioGroupItem value={value} id={id} />
              <Label htmlFor={id} className="block">
                {label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        <TableMoreFiltersMenu
          filterItems={[
            {
              title: 'District',
              filterComponent: (
                <TableFilterDropdown
                  title="District"
                  options={allOrgOptions}
                  selectedValues={selectedOrgs ?? []}
                  onChange={setSelectedOrgs}
                />
              ),
            },
            {
              title: 'School',
              filterComponent: (
                <TableFilterDropdown
                  title="School"
                  options={allSchoolOptions}
                  selectedValues={selectedSchools ?? []}
                  onChange={setSelectedSchools}
                />
              ),
            },
            {
              title: 'Class type',
              filterComponent: (
                <TableFilterDropdown
                  title="Class type"
                  options={classTimeTypesFilterOption}
                  selectedValues={selectedClassTypes ?? []}
                  onChange={setSelectedClassTypes}
                />
              ),
            },
            {
              title: 'Grade',
              filterComponent: (
                <TableFilterDropdown
                  title="Grade"
                  options={getStudentYearOptions()}
                  selectedValues={selectedGrades ?? []}
                  onChange={setSelectedGrades}
                />
              ),
            },
            {
              title: 'Lesson type',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson type"
                  options={lessonKindSchema.options.map((type) => ({
                    label: LessonKindToLabel[type],
                    value: type,
                  }))}
                  selectedValues={selectedLessonKinds ?? []}
                  onChange={setSelectedLessonKinds}
                />
              ),
            },
            {
              title: 'Remarks',
              filterComponent: (
                <TableFilterDropdown
                  title="Remarks"
                  options={coachSessionRemarkV2Schema.options.map((type) => ({
                    label: CoachSessionRemarkV2LabelMap[type],
                    value: type,
                  }))}
                  selectedValues={selectedSessionRemarksV2 ?? []}
                  onChange={setSelectedSessionRemarksV2}
                />
              ),
            },
            {
              title: 'Lesson name',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson name"
                  options={lessonTypeaheadSearch(queryClient)}
                  selectedValues={selectedLessons ?? []}
                  onChange={setSelectedLessons}
                />
              ),
            },
          ]}
          activeFiltersCount={activeFiltersCount}
        />
        {isFetching && !isPending && <LoaderIcon className="h-4 w-4" />}
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      viewMode,
      applyViewMode,
      allOrgOptions,
      selectedOrgs,
      setSelectedOrgs,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedGrades,
      setSelectedGrades,
      selectedLessons,
      setSelectedLessons,
      selectedLessonKinds,
      setSelectedLessonKinds,
      selectedClassTypes,
      setSelectedClassTypes,
      selectedSessionRemarksV2,
      setSelectedSessionRemarksV2,
      activeFiltersCount,
      queryClient,
      resetFilters,
      setQuery,
      isFetching,
      isPending,
    ],
  );

  const topBarRightComponents = <CreateSessionModal trigger={<Button>New session</Button>} />;

  const displayTimeZone = FrequentTimeZones.UsCentral;

  const sessionGroupBy = useMemo<EntityTableGroupBy<AdminSession, StartDateTimeRangeGroupKey>>(
    () => ({
      getGroupKey: (row) => groupByStartDateAndTimeRange(row.startInstant, row.endInstant, displayTimeZone),
      renderDivider: (groupKey, rowCount) => <SessionDateTimeDivider groupKey={groupKey} rowCount={rowCount} />,
    }),
    [displayTimeZone],
  );

  return (
    <InfoPage headerText="Sessions" tabs={sessionsTabs()}>
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onRowSelectionChange,
          getRowId,
          onPaginationChange,
        }}
        displayTimeZone={displayTimeZone}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
        from="today"
        paginationOptions={[10, 20, 30, 40, 50, 100]}
        groupBy={sessionGroupBy}
      />
    </InfoPage>
  );
}
